import React, { useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Checkbox, Avatar } from "@toddle-edu/ds-web";
import { TickDoubleOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import _ from "lodash";
import { useGradesContext } from "AppComponents/SharedEntities/Grades/context/GradesContext";
import { useCoursesContext } from "AppComponents/SharedEntities/Courses/context/CoursesContext";
import GradesList from "AppComponents/SharedEntities/Grades/components/GradesList";
import CoursesList from "AppComponents/SharedEntities/Courses/components/CoursesList";
import { PARTICIPANT_USER_TYPES } from "AppComponents/SharedEntities/Grades/modules/GradesConstants";
import { useSelectParticipantsContext } from "../../context/SelectParticipantsContext";
import { ACTIVE_MODE } from "../../modules/SelectParticipantsConstants";
import classes from "./LeftPanel.scss";

const USER_TYPE_COUNT_MAPPING = {
  STUDENT: "studentCount",
  STAFF: "staffCount",
  FAMILY_MEMBER: "familyCount",
};

const getSubTitle = ({
  allSchoolsCount,
  selectedUserTypes,
  t,
  allSchoolsCountLoading,
}) => {
  if (allSchoolsCountLoading || _.isEmpty(allSchoolsCount)) return null;
  return _.map(selectedUserTypes, (userType, index) => {
    const count = _.get(allSchoolsCount, USER_TYPE_COUNT_MAPPING[userType], 0);
    return `${index ? ", " : ""}${count} ${t(PARTICIPANT_USER_TYPES[userType], {
      count,
    })}`;
  }).join("");
};

// ─── SelectAllRow ─────────────────────────────────────────────────────────────

const SelectAllRow = ({
  testId,
  ariaLabel,
  label,
  subtitle,
  isAllSelected,
  isIndeterminate,
  onClick,
  avatarSize = "small",
  avatarProps = {},
}) => (
  <div
    className={classes.selectAllRow}
    onClick={onClick}
    onKeyDown={e => onEnterOrSpaceKeyDown(e, onClick)}
    role="button"
    tabIndex={0}
    data-test-id={`${testId}-selectAll`}
    aria-label={ariaLabel}
  >
    <div className={classes.selectAllLeft}>
      <Avatar
        dsVersion="2.0"
        shape="square"
        size={avatarSize}
        icon={<TickDoubleOutlined />}
        iconType="icon"
        color="neutral"
        {...avatarProps}
      />
      <div className={classes.selectAllInfo}>
        <span className={classNames("text-body", classes.selectAllLabel)}>
          {label}
        </span>
        {subtitle && (
          <span
            className={classNames("text-body-s", classes.selectAllSubtitle)}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>

    <div className={classes.marginRight}>
      <Checkbox
        isChecked={isAllSelected}
        isIndeterminate={isIndeterminate}
        testId={`${testId}-selectAll-checkbox`}
      />
    </div>
  </div>
);

const GradesSelectAllRow = ({ testId }) => {
  const { t } = useI18n(["common"]);
  const {
    filteredSchoolGradeIds,
    selectedGradeList,
    setSelectedGradeList,
    gradeDetailsMap,
    allSchoolsCount,
    allSchoolsCountLoading,
    selectedUserTypes,
    hierarchyLoading,
  } = useGradesContext();

  const totalCount = _.size(filteredSchoolGradeIds);
  const selectedCount = _.size(_.uniqBy(selectedGradeList, "id"));
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const handleClick = useCallback(() => {
    if (isAllSelected) {
      setSelectedGradeList([]);
    } else {
      setSelectedGradeList(
        _.map(filteredSchoolGradeIds, id => ({
          id,
          name: _.get(gradeDetailsMap, `${id}.name`, ""),
        }))
      );
    }
  }, [
    isAllSelected,
    filteredSchoolGradeIds,
    gradeDetailsMap,
    setSelectedGradeList,
  ]);

  const subtitle = getSubTitle({
    allSchoolsCount,
    selectedUserTypes,
    t,
    allSchoolsCountLoading,
  });

  if (hierarchyLoading) return null;

  return (
    <SelectAllRow
      testId={testId}
      ariaLabel={t("common:select_all_grades")}
      label={t("common:all_grades_with_count", { count: totalCount })}
      subtitle={subtitle}
      isAllSelected={isAllSelected}
      isIndeterminate={isIndeterminate}
      onClick={handleClick}
      avatarSize="medium"
      avatarProps={{ color: "neutral" }}
      withMarginRight
    />
  );
};

const CoursesSelectAllRow = ({ testId }) => {
  const { t } = useI18n(["common"]);
  const {
    filteredSchoolCourseIds,
    selectedCourseList,
    setSelectedCourseList,
    courseDetailsMap,
    allSchoolsCount,
    allSchoolsCountLoading,
    selectedUserTypes,
    hierarchyLoading,
  } = useCoursesContext();

  const totalCount = _.size(filteredSchoolCourseIds);
  const selectedCount = _.size(_.uniqBy(selectedCourseList, "id"));

  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const handleClick = useCallback(() => {
    if (isAllSelected) {
      setSelectedCourseList([]);
    } else {
      setSelectedCourseList(
        _.map(filteredSchoolCourseIds, id => ({
          id,
          title: _.get(courseDetailsMap, `${id}.title`, ""),
        }))
      );
    }
  }, [
    isAllSelected,
    filteredSchoolCourseIds,
    courseDetailsMap,
    setSelectedCourseList,
  ]);

  const subtitle = getSubTitle({
    allSchoolsCount,
    selectedUserTypes,
    t,
    allSchoolsCountLoading,
  });

  if (hierarchyLoading) return null;

  return (
    <SelectAllRow
      testId={testId}
      ariaLabel={t("common:select_all_courses")}
      label={t("common:all_classes_with_count", { count: totalCount })}
      subtitle={subtitle}
      isAllSelected={isAllSelected}
      isIndeterminate={isIndeterminate}
      onClick={handleClick}
    />
  );
};

const LeftPanel = ({ testId }) => {
  const { searchText, activeMode } = useSelectParticipantsContext();
  const componentTestId = testId || "selectParticipantsModal";

  return (
    <div className={classes.leftPanel}>
      {!searchText ? (
        <>
          {activeMode === ACTIVE_MODE.GRADES && (
            <GradesSelectAllRow testId={componentTestId} />
          )}
          {activeMode === ACTIVE_MODE.CLASSES && (
            <CoursesSelectAllRow testId={componentTestId} />
          )}
          <div className={classes.separator} />
        </>
      ) : null}

      <div className={classes.scrollContainer}>
        {activeMode === ACTIVE_MODE.GRADES && (
          <GradesList testId={componentTestId} />
        )}
        {activeMode === ACTIVE_MODE.CLASSES && (
          <CoursesList testId={componentTestId} />
        )}
      </div>
    </div>
  );
};

LeftPanel.propTypes = {
  testId: PropTypes.string,
};

LeftPanel.defaultProps = {
  testId: undefined,
};

export default LeftPanel;
